import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import AuthMethodSelector from './components/AuthMethodSelector';
import AuthenticatorSetup from './components/AuthenticatorSetup';
import SMSSetup from './components/SMSSetup';
import BackupCodesDisplay from './components/BackupCodesDisplay';
import VerificationTest from './components/VerificationTest';
import SecurityEducation from './components/SecurityEducation';
import { logger } from '../../utils/logger';

const TwoFactorAuthenticationSetup = () => {
  const navigate = useNavigate();
  const { user, userProfile, updateProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState('method-selection');
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [setupComplete, setSetupComplete] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (userProfile?.twoFactorAuth) {
      setSetupComplete(true);
      setCurrentStep('complete');
    }
  }, [userProfile]);

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    setCurrentStep('setup');
    setError('');
  };

  const handleSetupComplete = async (setupData) => {
    try {
      setLoading(true);
      setError('');

      // Generate backup codes
      const codes = generateBackupCodes();
      setBackupCodes(codes);

      // Update user profile to enable 2FA
      const { error: updateError } = await updateProfile({
        two_factor_auth: true,
        two_factor_method: selectedMethod
      });

      if (updateError) throw updateError;

      logger?.audit('TWO_FACTOR_ENABLED', user?.id, { method: selectedMethod });
      setCurrentStep('backup-codes');
    } catch (err) {
      setError('Failed to enable two-factor authentication. Please try again.');
      logger?.error('2FA setup failed', err, { userId: user?.id });
    } finally {
      setLoading(false);
    }
  };

  const handleBackupCodesConfirmed = () => {
    setCurrentStep('verification-test');
  };

  const handleVerificationSuccess = () => {
    setSetupComplete(true);
    setCurrentStep('complete');
  };

  const handleDisable2FA = async () => {
    try {
      setLoading(true);
      const { error: updateError } = await updateProfile({
        two_factor_auth: false,
        two_factor_method: null
      });

      if (updateError) throw updateError;

      logger?.audit('TWO_FACTOR_DISABLED', user?.id);
      setSetupComplete(false);
      setCurrentStep('method-selection');
      setSelectedMethod(null);
    } catch (err) {
      setError('Failed to disable two-factor authentication.');
    } finally {
      setLoading(false);
    }
  };

  const generateBackupCodes = () => {
    const codes = [];
    for (let i = 0; i < 10; i++) {
      const code = Math.random()?.toString(36)?.substring(2, 10)?.toUpperCase();
      codes?.push(code);
    }
    return codes;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'method-selection':
        return (
          <div className="space-y-6">
            <SecurityEducation />
            <AuthMethodSelector
              onMethodSelect={handleMethodSelect}
              selectedMethod={selectedMethod}
            />
          </div>
        );

      case 'setup':
        return (
          <div className="space-y-6">
            {selectedMethod === 'authenticator' && (
              <AuthenticatorSetup
                onComplete={handleSetupComplete}
                onBack={() => setCurrentStep('method-selection')}
                loading={loading}
              />
            )}
            {selectedMethod === 'sms' && (
              <SMSSetup
                onComplete={handleSetupComplete}
                onBack={() => setCurrentStep('method-selection')}
                loading={loading}
              />
            )}
          </div>
        );

      case 'backup-codes':
        return (
          <BackupCodesDisplay
            codes={backupCodes}
            onConfirm={handleBackupCodesConfirmed}
          />
        );

      case 'verification-test':
        return (
          <VerificationTest
            method={selectedMethod}
            onSuccess={handleVerificationSuccess}
            onBack={() => setCurrentStep('backup-codes')}
          />
        );

      case 'complete':
        return (
          <div className="text-center space-y-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-success/10 rounded-full">
              <Icon name="CheckCircle2" size={48} className="text-success" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Two-Factor Authentication Enabled
              </h2>
              <p className="text-muted-foreground">
                Your account is now protected with two-factor authentication
              </p>
            </div>
            <div className="bg-card border border-border rounded-lg p-6 text-left">
              <div className="flex items-start gap-3 mb-4">
                <Icon name="Shield" size={20} className="text-primary mt-1" />
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Active Method</h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedMethod === 'authenticator' ? 'Authenticator App' : 'SMS Verification'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="Key" size={20} className="text-primary mt-1" />
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Backup Codes</h3>
                  <p className="text-sm text-muted-foreground">
                    {backupCodes?.length} backup codes generated and saved
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="outline"
                onClick={handleDisable2FA}
                loading={loading}
                iconName="XCircle"
              >
                Disable 2FA
              </Button>
              <Button
                onClick={() => navigate('/settings-and-privacy')}
                iconName="ArrowRight"
                iconPosition="right"
              >
                Back to Settings
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getStepNumber = () => {
    const steps = {
      'method-selection': 1,
      'setup': 2,
      'backup-codes': 3,
      'verification-test': 4,
      'complete': 5
    };
    return steps?.[currentStep] || 1;
  };

  return (
    <>
      <Helmet>
        <title>Two-Factor Authentication Setup - MyBodyToday</title>
        <meta name="description" content="Secure your account with two-factor authentication" />
      </Helmet>
      <div className="min-h-screen bg-background pb-20">
        {/* Header */}
        <div className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/settings-and-privacy')}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Back to settings"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div className="flex-1">
                <h1 className="text-xl font-bold text-foreground">Two-Factor Authentication</h1>
                <p className="text-sm text-muted-foreground">Secure your account with 2FA</p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Indicator */}
        {currentStep !== 'complete' && (
          <div className="max-w-4xl mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-2">
              {[1, 2, 3, 4]?.map((step) => (
                <div key={step} className="flex items-center flex-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      getStepNumber() >= step
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {step}
                  </div>
                  {step < 4 && (
                    <div
                      className={`flex-1 h-1 mx-2 ${
                        getStepNumber() > step ? 'bg-primary' : 'bg-muted'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Method</span>
              <span>Setup</span>
              <span>Backup</span>
              <span>Test</span>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="max-w-4xl mx-auto px-4 mb-6">
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{error}</p>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 py-6">
          {renderStepContent()}
        </div>
      </div>
      <BottomTabNavigation />
    </>
  );
};

export default TwoFactorAuthenticationSetup;